// NEW_FILE_JUSTIFIED: Dynamic list field for policy application forms — add/remove entries with sub-field rendering and maxEntries support; no existing repeatable group component
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FieldRenderer } from './FieldRenderer';
import type {
  IRepeatableGroupFieldProps,
  IRepeatableGroupEntry,
} from '@/@types/policyFormTemplates';

const GROUP_GRID_COLS: Record<number, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
};

const COL_SPAN_CLASS: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

export function RepeatableGroupField({
  field,
  entries,
  onChange,
  disabled,
}: IRepeatableGroupFieldProps) {
  const groupFields = field.groupFields;
  const minEntries = field.minEntries ?? 0;

  useEffect(() => {
    if (groupFields && entries.length < minEntries) {
      const toAdd = minEntries - entries.length;
      const newEntries: IRepeatableGroupEntry[] = Array.from({ length: toAdd }, () => ({
        id: crypto.randomUUID(),
        values: {},
      }));
      onChange([...entries, ...newEntries]);
    }
  }, [minEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!groupFields) return null;

  const canAdd = !field.maxEntries || entries.length < field.maxEntries;
  const entryLabel = field.entryLabel ?? 'Entry';
  const useColSpan = groupFields.some(f => f.groupColSpan !== undefined);

  const addEntry = () => {
    if (!canAdd) return;
    onChange([...entries, { id: crypto.randomUUID(), values: {} }]);
  };

  const removeEntry = (id: string) => {
    onChange(entries.filter(e => e.id !== id));
  };

  const updateEntry = (id: string, key: string, value: unknown) => {
    onChange(entries.map(e => (e.id === id ? { ...e, values: { ...e.values, [key]: value } } : e)));
  };

  return (
    <div className="space-y-3">
      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground">No entries added yet.</p>
      )}

      {entries.map((entry, index) => (
        <Card key={entry.id} className="overflow-hidden py-0 gap-0">
          <CardHeader className="bg-muted border-b py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm">
                  {entryLabel} {index + 1}
                </CardTitle>
                {entries.length > 1 && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    {index + 1} of {entries.length}
                  </span>
                )}
              </div>
              {!disabled && entries.length > minEntries && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeEntry(entry.id)}
                  className="gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent
            className={cn(
              'grid grid-cols-1 gap-4 p-4',
              useColSpan
                ? 'md:grid-cols-12'
                : field.groupColumns
                  ? GROUP_GRID_COLS[field.groupColumns]
                  : undefined
            )}
          >
            {groupFields.map(subField => (
              <div
                key={subField.key}
                className={
                  useColSpan && subField.groupColSpan
                    ? COL_SPAN_CLASS[subField.groupColSpan]
                    : undefined
                }
              >
                <FieldRenderer
                  field={subField}
                  value={entry.values[subField.key]}
                  onChange={(key, val) => updateEntry(entry.id, key, val)}
                  allValues={entry.values}
                  disabled={disabled}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {!disabled && canAdd && (
        <div className="flex justify-center pt-1">
          <Button type="button" onClick={addEntry} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Another {entryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
